import React from 'react';
import makeStyles from '@material-ui/styles/makeStyles';
import TotalOrders from '../../Analytics/TotalsListWidget/TotalsListItem/Icons/TotalOrders';
import RevIcon from '../../Analytics/TotalsListWidget/TotalsListItem/Icons/Revenue';
import TotalTipsIcon from '../../Analytics/TotalsListWidget/TotalsListItem/Icons/TotalTips';
import TotalRefundedIcon from '../../Analytics/TotalsListWidget/TotalsListItem/Icons/TotalRefunded';

import CustomerMainDetailsCard from './CustomerMainDetailsCard';
import CustomerOrderHistory from './CustomerOrderHistory';
import TotalItem from './TotalItem';
import { ICustomerOrderItem } from './CustomerOrderItem';

import CategorySummaryTopFive, { IItemCard } from '../../Analytics/CategorySummaryWidget/CategorySummaryTopFive';
import useFormatCurrency from '../../../localisation/useCurrency';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
    root: {},
    favourites: {
        borderRadius: '1rem',
        border: '1px solid #EEEEEE',
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: '#FFFFFF',
        maxHeight: '600px',
        minWidth: '19.125rem',
        '&>div': {
            padding: '1.25rem',
            '&>div': {
                fontSize: '0.9375rem',
                lineHeight: '1.25rem',
                color: '#8D98B7',
                marginBottom: '1.875rem',
            },
            '&>ul': {
                padding: '0.3125rem',
                '&>div>div>div:first-child': {
                    '&>div:first-child': {
                        flex: 'unset!important',
                        width: '3.4375rem!important',
                        minWidth: '3.4375rem!important',
                        height: '3.4375rem!important',
                    },
                    '&>div:last-child': {
                        paddingLeft: '0.75rem',
                        '&>div:first-child': {
                            fontSize: '1rem',
                            lineHeight: '1.375rem',
                            color: '#212121',
                            paddingLeft: '0!important',
                        },
                    },
                },
            },
        },
    },
    totals_list: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-around',
        '@media only screen and (max-width: 768px)': {
            display: 'grid!important',
            gridTemplateColumns: '50% 50%',
        },
    },
    title: {
        padding: '0.375rem',
        fontSize: '1.125rem',
        fontWeight: 600,
    },
    close: {
        padding: '0.375rem',
        marginRight: '0.75rem',
        cursor: 'pointer',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid #E8EAF4',
    },
    divider: {
        height: '5.0625rem',
        backgroundColor: '#E8EAF4',
        flex: '0 0 1px',
        '@media only screen and (max-width: 768px)': {
            display: 'none!important',
        },
    },
    ordersWrapper: {
        display: 'grid',
        gridTemplateColumns: '60% 40%',
        '@media only screen and (max-width: 1023px)': {
            display: 'flex!important',
            flexDirection: 'column!important',
        },
    },
    orderHistoryWrapper: {
        borderRadius: '1rem',
        overflow: 'hidden',
        border: '1px solid #EEEEEE',
        maxHeight: '63vh',
        marginRight: '1.25rem',
        minWidth: '28.125rem',
        '@media only screen and (max-width: 800px)': {
            marginRight: '0!important',
            marginBottom: '1.25rem',
        },
    },
    orderHistory: {
        position: 'relative',
        padding: '1.25rem',
        boxSizing: 'border-box',
    },
});

export interface IFavourite {
    menu_item_id: string;
    name_public: string;
    total_ordered: number;
    total_revenue: number;
    url_photo: string;
}

export interface ICustomer {
    first_name: string;
    last_name: string;
    contact_phone?: string;
    email?: string;
    total_spend: number;
    total_orders: number;
    total_refunded: number;
    total_tips: number;
    top_items: IFavourite[];
    order_history: ICustomerOrderItem[];
}

export interface ICustomerModal {
    data: ICustomer;
    onSelectItem: (id: string) => void;
}

const ConsumerModal = (props: ICustomerModal) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { formatCurrency } = useFormatCurrency();
    function renderFavourites() {
        let favourites: IItemCard[] = [];
        props.data.top_items.forEach((item: IFavourite, index) => {
            favourites.push({
                id: index,
                name: item.name_public,
                revenue: formatCurrency(item.total_revenue),
                total: item.total_ordered.toString(),
                photoUrl: item.url_photo,
                category: '',
            });
        });
        return (
            <CategorySummaryTopFive
                title={t('customers.TopFive.title')}
                items={favourites}
                label={t('customers.TopFive.label')}
            />
        );
    }
    return (
        <div className={classes.root}>
            <div className={classes.totals_list}>
                <CustomerMainDetailsCard
                    name={`${props.data.first_name} ${props.data.last_name}`}
                    number={props.data.contact_phone}
                    email={props.data.email}
                />
                <div className={classes.divider}></div>
                <TotalItem
                    label={t('customers.modal.TotalOrders')}
                    value={props.data.total_orders}
                    icon={<RevIcon />}
                    tooltipTxt={t('customers.modal.TotalOrdersTooltipTxt')}
                    isCurrency={false}
                />
                <TotalItem
                    label={t('customers.modal.TotalSpent')}
                    value={props.data.total_spend}
                    icon={<TotalOrders />}
                    tooltipTxt={t('customers.modal.TotalSpentTooltipTxt')}
                    isCurrency={true}
                />
                <TotalItem
                    label={t('customers.modal.TotalTips')}
                    value={props.data.total_tips}
                    icon={<TotalTipsIcon />}
                    tooltipTxt={t('customers.modal.TotalTipsTooltipTxt')}
                    isCurrency={true}
                />
                <TotalItem
                    label={t('customers.modal.TotalRefunded')}
                    value={props.data.total_refunded}
                    icon={<TotalRefundedIcon />}
                    tooltipTxt={t('customers.modal.TotalRefundedTooltipTxt')}
                    isCurrency={true}
                />
            </div>
            <div className={classes.ordersWrapper} data-component-name={'ORDERS_SECTION_CONTAINER'}>
                <div className={classes.orderHistoryWrapper}>
                    <div className={classes.orderHistory}>
                        <CustomerOrderHistory items={props.data.order_history} onSelectItem={props.onSelectItem} />
                    </div>
                </div>

                <div className={classes.favourites} data-component-name={'FAVOURITES_TOP_FIVE'}>
                    {renderFavourites()}
                </div>
            </div>
        </div>
    );
};

export default ConsumerModal;
